/**
 * The record treatment (designer handoff — "Vinyl Covers").
 *
 * The refined vinyl read that replaces the old radial-gradient grooves: a
 * proper SVG record — navy field, 22 concentric groove rings, two bright rim
 * rings and a centre label — drawn on a 680×680 viewBox and scaled into the
 * disc-fitted porthole box (`.jacket-porthole-face`, a 2·--hole-r square
 * centred on the hole), so its radii track the responsive disc for free.
 *
 * It rides at ~70% opacity OVER the media (the colour poster/live loop shows
 * through at ~30%), which is the brief: keep the media in the record, under a
 * navy overlay carrying the grooves. The parent (PortholeTexture) owns the
 * `circle()` clip to the hole and composites the dot screen + grain above it,
 * so only the right crescent past the opaque jacket ever shows.
 *
 * The open audio stage mounts DiscArt directly and SPINS it (record-sleeve-
 * shell's SpinningRecord). The concentric groove art alone is rotationally
 * symmetric — turning it is invisible — so the stage passes the asymmetric
 * marks below (a printed label in the sleeve's own colourway, a spindle hole,
 * an off-centre catalog mark, and a soft glint that sweeps as it turns). The
 * porthole `Disc` passes none of them, so its read is unchanged.
 */

import { useId } from "react"

import { MONO } from "./type-voices"

const GROOVE_RINGS = Array.from({ length: 22 }, (_, i) => 70 + i * 12)

/** The sleeve colourway, threaded onto the disc's centre label. */
interface DiscLabel {
  base: string
  ink: string
  glow: string
}

/**
 * The bare groove art on a 680×680 viewBox — everything concentric on
 * (340, 340), so it spins as one rigid unit with no wobble. The porthole
 * (`Disc`) fits it into the die-cut; the open audio stage mounts it directly
 * as the spinning record behind the content card.
 *
 * The optional marks (`label`, `catalog`, `spindle`, `sheen`) break the radial
 * symmetry so the spin actually reads — they default off, so the porthole read
 * is untouched.
 */
export function DiscArt({
  opacity = 0.7,
  label,
  catalog,
  spindle = false,
  sheen = false,
}: {
  opacity?: number
  label?: DiscLabel
  catalog?: string
  spindle?: boolean
  sheen?: boolean
}) {
  // Instance-scoped gradient id so two discs on the page can't collide (only
  // one SpinningRecord mounts at a time today, but useId keeps it honest).
  const glintId = useId()
  const labelBase = label?.base ?? "#28395f"

  return (
    <svg
      viewBox="0 0 680 680"
      preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", height: "100%", display: "block", opacity }}
    >
      {sheen && (
        <defs>
          {/* The raking-light glint — a soft white bloom, brightest at its
              core and gone by the rim. It rides inside DiscArt, so the spin
              carries it around the record like light off turning vinyl. */}
          <radialGradient id={glintId}>
            <stop offset="0%" stopColor="#ffffff" stopOpacity={0.22} />
            <stop offset="55%" stopColor="#ffffff" stopOpacity={0.08} />
            <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
          </radialGradient>
        </defs>
      )}

      {/* The navy field. */}
      <circle cx={340} cy={340} r={338} fill="#2f4370" />
      {/* 22 groove rings. */}
      {GROOVE_RINGS.map((r, i) => (
        <circle
          key={i}
          cx={340}
          cy={340}
          r={r}
          fill="none"
          stroke="#22345f"
          strokeWidth={1.4}
          strokeOpacity={0.5}
        />
      ))}
      {/* Two bright rim rings catching the light. */}
      <circle
        cx={340}
        cy={340}
        r={300}
        fill="none"
        stroke="#93a3ca"
        strokeWidth={1.4}
        strokeOpacity={0.55}
      />
      <circle
        cx={340}
        cy={340}
        r={286}
        fill="none"
        stroke="#93a3ca"
        strokeWidth={1}
        strokeOpacity={0.35}
      />

      {/* The glint sits over the grooves but under the label — a highlight on
          the vinyl, not on the paper. Off-centre so it orbits as it spins. */}
      {sheen && <circle cx={248} cy={222} r={172} fill={`url(#${glintId})`} />}

      {/* The centre label (rides behind the opaque jacket at rest). Recoloured
          to the sleeve's colourway when the stage passes one, so the record
          reads as the printed disc that belongs in this jacket. */}
      <circle cx={340} cy={340} r={116} fill={labelBase} />
      {label && (
        // A thin bright rim inside the label edge — a pressed paper label.
        <circle
          cx={340}
          cy={340}
          r={109}
          fill="none"
          stroke={label.glow}
          strokeWidth={2}
          strokeOpacity={0.5}
        />
      )}

      {/* The off-centre catalog mark — printed above the spindle, so it swings
          around the centre as the record turns (the clearest rotation cue). */}
      {catalog && (
        <text
          x={340}
          y={302}
          textAnchor="middle"
          style={{
            fontFamily: MONO,
            fontWeight: 700,
            fontSize: 30,
            letterSpacing: "0.12em",
            fill: label?.glow ?? "#93a3ca",
          }}
        >
          {catalog}
        </text>
      )}

      {/* The spindle hole — a small paper-toned void at the exact centre;
          centred, so it reads as authenticity, not as a motion cue. */}
      {spindle && (
        <>
          <circle cx={340} cy={340} r={10} fill="#efe7d6" />
          <circle
            cx={340}
            cy={340}
            r={10}
            fill="none"
            stroke="#000000"
            strokeWidth={1}
            strokeOpacity={0.25}
          />
        </>
      )}
    </svg>
  )
}

export function Disc({ opacity = 0.7 }: { opacity?: number }) {
  return (
    <div
      aria-hidden="true"
      className="jacket-porthole-face pointer-events-none"
    >
      <DiscArt opacity={opacity} />
    </div>
  )
}
